import Box from '@mui/material/Box';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { toSharePointHref } from '../../utils/sharepointUrl';
import CreateProjectModal from './CreateProjectModal';
import FabricationLocationsModal from '../ProgressTracking/Piping/Admin/FabricationLocationsModal';
import DeleteProjectModal from './DeleteProjectModal';
import PrioritiesModal from '../ProgressTracking/Piping/Admin/PrioritiesModal';
import DivisionManagement from './DivisionManagement';
import UserProvisioningTab from './UserProvisioningTab';
import DisciplinesModal from './DisciplinesModal';
import SubDepartmentsModal from './SubDepartmentsModal';
import ProjectDetailsModal from './ProjectDetailsModal';
import { usePermissions } from '../../hooks/usePermissions';
import AuditLogTab from './AuditLogTab';
import PermissionsTab from './PermissionsTab';
import DataTab from './DataTab';
import WeeklyReportManager from './WeeklyReportManager';
import ChatbotSettingsModal from './ChatbotSettingsModal';
import AccountingSettingsModal from './AccountingSettingsModal';
import LetterheadSettingsModal from './LetterheadSettingsModal';
import NomenclatureSettingsModal from './NomenclatureSettingsModal';
import ManageTagsModal from './ManageTagsModal';
import EstimateManagement from './EstimateManagement';
import ProjectManagementHelpModal from './ProjectManagementHelpModal';
import HiringLayoffDashboard from '../Accounting/HiringLayoffDashboard';
import NotificationRecipientsPage from '../Development/NotificationRecipientsPage';
import EmailLogPage from '../Development/EmailLogPage';
import FridayUpdateRequestsPage from './FridayUpdateRequestsPage';
import MaestroActualsImportModal from './MaestroActualsImportTab';
import useTableSort from '../../hooks/useTableSort';
import { FilterTemplateDropdown, FilterIcon, useFilterTemplate } from '../common/FilteringTemplate';

const ADMIN_TABS = new Set([
  'estimates', 'projects', 'users', 'employees', 'divisions', 'permissions',
  'settings', 'audit-log', 'notifications', 'weekly-report',
]);

const SETTINGS_CARD = {
  padding: '1.5rem',
  border: '1px solid #e9ecef',
  borderRadius: '8px',
  backgroundColor: '#f8f9fa',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  boxSizing: 'border-box',
};

const SETTINGS_CARD_BUTTON = {
  padding: '0.75rem 1rem',
  marginTop: 'auto',
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: '#d5dbe1',
  color: '#1f2937',
  border: '1px solid #b7c0c8',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: '500',
};

// Old bookmarks: Roles and Authority Levels now live inside the Permissions tab
// (PermissionsTab reads the legacy value to open the matching sub-tab).
// Data is the Selected Contractors list, now under Settings.
const LEGACY_TAB_MAP = { roles: 'permissions', 'authority-levels': 'permissions', data: 'settings' };

const AdminPanel = ({ onClose, user }) => {
  const { instance, accounts } = useMsal();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  
  // Use centralized permission hook
  const permissions = usePermissions(user);
  const { isAdmin, canAssignRoles, isSuperadmin } = permissions;
  
  // Default to users tab if admin, otherwise projects. URL ?tab= survives refresh.
  const [activeTab, setActiveTabState] = useState(() => {
    const raw = searchParams.get('tab');
    const fromUrl = LEGACY_TAB_MAP[raw] || raw;
    if (fromUrl && ADMIN_TABS.has(fromUrl)) return fromUrl;
    return isAdmin ? 'users' : 'projects';
  });
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const raw = searchParams.get('tab');
    const fromUrl = LEGACY_TAB_MAP[raw] || raw;
    if (fromUrl && ADMIN_TABS.has(fromUrl) && fromUrl !== activeTab) {
      setActiveTabState(fromUrl);
    }
  }, [searchParams, activeTab]);
  // Sub-tab for Notifications (recipients roster + email send log)
  const [notifSubTab, setNotifSubTab] = useState('recipients');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProjectsHelp, setShowProjectsHelp] = useState(false);
  const [showFabricationLocationsModal, setShowFabricationLocationsModal] = useState(false);
  const [showPrioritiesModal, setShowPrioritiesModal] = useState(false);
  const [showChatbotSettingsModal, setShowChatbotSettingsModal] = useState(false);
  const [showMaestroImportModal, setShowMaestroImportModal] = useState(() => searchParams.get('maestro') === '1');
  const [showAccountingSettingsModal, setShowAccountingSettingsModal] = useState(false);
  const [showLetterheadSettingsModal, setShowLetterheadSettingsModal] = useState(false);
  const [showNomenclatureSettingsModal, setShowNomenclatureSettingsModal] = useState(false);
  const [showDisciplinesModal, setShowDisciplinesModal] = useState(false);
  const [showManageTagsModal, setShowManageTagsModal] = useState(false);
  const [showSubDepartmentsModal, setShowSubDepartmentsModal] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [showProjectDetailsModal, setShowProjectDetailsModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectSearch, setProjectSearch] = useState('');
  // Which set of projects to show: active (default), archived, or all. Superadmin only.
  const [projectView, setProjectView] = useState('active');

  const projectColumns = useMemo(() => [
    { key: 'job_number', label: 'Job Number', type: 'text' },
    { key: 'job_name', label: 'Job Name', type: 'text' },
    { key: 'client_name', label: 'Client', type: 'text' },
    { key: 'division_display', label: 'Division', type: 'text' },
    { key: 'disciplines_display', label: 'Disciplines', type: 'text' },
    { key: 'created_display', label: 'Created', type: 'date' },
  ], []);

  const projectSort = useTableSort({ columns: projectColumns });

  const flatProjects = useMemo(() => projects.map(p => ({
    ...p,
    division_display: p.division_name ? `${p.division_number} - ${p.division_name}` : '',
    disciplines_display: p.disciplines?.length > 0 ? p.disciplines.map(d => d.discipline_code).join(', ') : '',
    created_display: p.created_at ? new Date(p.created_at).toLocaleDateString() : '',
  })), [projects]);

  const projectFilter = useFilterTemplate({ items: flatProjects, columns: projectColumns });

  const filteredProjects = useMemo(() => {
    let result = projectFilter.filteredItems;
    if (projectSearch.trim()) {
      const q = projectSearch.toLowerCase();
      result = result.filter(p =>
        p.job_number?.toLowerCase().includes(q) ||
        p.job_name?.toLowerCase().includes(q) ||
        p.client_name?.toLowerCase().includes(q) ||
        p.division_display?.toLowerCase().includes(q) ||
        p.disciplines_display?.toLowerCase().includes(q) ||
        p.created_display?.toLowerCase().includes(q)
      );
    }
    return projectSort.applySorting(result);
  }, [projectFilter.filteredItems, projectSearch, projectSort]);

  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true);
      const url = projectView === 'active' ? '/projects' : `/projects?archived=${projectView}`;
      const projectsResponse = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        url
      );
      setProjects(projectsResponse.data.projects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
    setProjectsLoading(false);
  }, [instance, accounts, projectView]);

  useEffect(() => {
    if (activeTab === 'projects') {
      fetchProjects();
    }
  }, [activeTab, fetchProjects]);

  const handleDeleteProject = (project) => {
    setSelectedProject(project);
    setShowDeleteProjectModal(true);
  };

  const handleManageProject = (project) => {
    setSelectedProject(project);
    setShowProjectDetailsModal(true);
  };

  const handleProjectUpdated = () => {
    fetchProjects();
  };

  const handleProjectDeleted = (projectId) => {
    setProjects(prev => prev.filter(project => project.id !== projectId));
  };

  const handleArchiveProject = async (project) => {
    if (!window.confirm(`Archive project "${project.job_number} - ${project.job_name}"?\n\nIt will be hidden from the active project list and from all users, but no data is deleted. You can restore it at any time.`)) {
      return;
    }
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/projects/${project.id}/archive`);
      fetchProjects();
    } catch (error) {
      console.error('Error archiving project:', error);
      window.alert(error.response?.data?.message || 'Failed to archive project');
    }
  };

  const handleRestoreProject = async (project) => {
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/projects/${project.id}/restore`);
      fetchProjects();
    } catch (error) {
      console.error('Error restoring project:', error);
      window.alert(error.response?.data?.message || 'Failed to restore project');
    }
  };

  const handleProjectCreated = (newProject) => {
    setProjects(prev => [newProject, ...prev]);
  };

  // Get panel title based on role
  const getPanelTitle = () => {
    if (isAdmin) return 'Admin Configuration';
    return 'Configuration';
  };

  return (
    <Box sx={{ padding: { xs: '1rem 0.75rem', md: '2rem' }, minWidth: 0 }}>
      {/* Admin Panel Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ margin: 0, color: '#333' }}>
          {getPanelTitle()}
        </h2>
      </div>

      {/* Tab Navigation.
          Eight tabs at 1.5rem padding cannot fit a phone. Without a scroll container the
          flex row compressed each button below its label width and the labels collided
          ("EstimatesProjects Users..."). The row now scrolls sideways as a tab strip, and
          minWidth: max-content stops the buttons being squeezed at all. */}
      <div style={{ 
        borderBottom: '2px solid #e9ecef',
        marginBottom: '2rem',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}>
        <div style={{ display: 'flex', gap: '1rem', minWidth: 'max-content' }}>
          <button
            onClick={() => setActiveTab('estimates')}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              backgroundColor: 'transparent',
              borderBottom: activeTab === 'estimates' ? '2px solid #007bff' : '2px solid transparent',
              color: activeTab === 'estimates' ? '#007bff' : '#666',
              cursor: 'pointer',
              fontSize: '1rem',
              textTransform: 'capitalize'
            }}
          >
            Estimates
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              backgroundColor: 'transparent',
              borderBottom: activeTab === 'projects' ? '2px solid #007bff' : '2px solid transparent',
              color: activeTab === 'projects' ? '#007bff' : '#666',
              cursor: 'pointer',
              fontSize: '1rem',
              textTransform: 'capitalize'
            }}
          >
            Projects
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('users')}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: activeTab === 'users' ? '2px solid #007bff' : '2px solid transparent',
                color: activeTab === 'users' ? '#007bff' : '#666',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Users
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('employees')}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: activeTab === 'employees' ? '2px solid #007bff' : '2px solid transparent',
                color: activeTab === 'employees' ? '#007bff' : '#666',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Employees
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('divisions')}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: activeTab === 'divisions' ? '2px solid #007bff' : '2px solid transparent',
                color: activeTab === 'divisions' ? '#007bff' : '#666',
                cursor: 'pointer',
                fontSize: '1rem',
                textTransform: 'capitalize'
              }}
            >
              Divisions
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('permissions')}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: activeTab === 'permissions' ? '2px solid #007bff' : '2px solid transparent',
                color: activeTab === 'permissions' ? '#007bff' : '#666',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Permissions
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('settings')}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: activeTab === 'settings' ? '2px solid #007bff' : '2px solid transparent',
                color: activeTab === 'settings' ? '#007bff' : '#666',
                cursor: 'pointer',
                fontSize: '1rem',
                textTransform: 'capitalize'
              }}
            >
              Settings
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('audit-log')}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: activeTab === 'audit-log' ? '2px solid #007bff' : '2px solid transparent',
                color: activeTab === 'audit-log' ? '#007bff' : '#666',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Audit Log
            </button>
          )}
          {isSuperadmin && (
            <button
              onClick={() => setActiveTab('notifications')}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: activeTab === 'notifications' ? '2px solid #007bff' : '2px solid transparent',
                color: activeTab === 'notifications' ? '#007bff' : '#666',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Notifications
            </button>
          )}
          {isSuperadmin && (
            <button
              onClick={() => setActiveTab('weekly-report')}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: activeTab === 'weekly-report' ? '2px solid #007bff' : '2px solid transparent',
                color: activeTab === 'weekly-report' ? '#007bff' : '#666',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Weekly Report
            </button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'projects' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <h3 style={{ margin: 0 }}>Project Management</h3>
              <button
                onClick={() => setShowProjectsHelp(true)}
                style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  backgroundColor: '#6c757d', color: 'white', border: 'none',
                  cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
                title="How jobs are created, linked to estimates, and corrected"
              >
                ?
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              {isSuperadmin && (
                <div style={{ display: 'inline-flex', border: '1px solid #ced4da', borderRadius: '4px', overflow: 'hidden' }}>
                  {[
                    { key: 'active', label: 'Active' },
                    { key: 'archived', label: 'Archived' },
                    { key: 'all', label: 'All' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setProjectView(opt.key)}
                      style={{
                        padding: '0.45rem 0.85rem', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
                        backgroundColor: projectView === opt.key ? '#007bff' : 'white',
                        color: projectView === opt.key ? 'white' : '#495057',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              <input
                type="text"
                placeholder="Search projects..."
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                style={{
                  padding: '0.45rem 0.75rem', border: '1px solid #ced4da', borderRadius: '4px',
                  fontSize: '0.9rem', width: '240px', boxSizing: 'border-box'
                }}
              />
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white',
                  border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem'
                }}
              >
                + Create Project
              </button>
            </div>
          </div>
          
          {projectsLoading ? (
            <div style={{ padding: '2rem', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textAlign: 'center' }}>
              Loading projects...
            </div>
          ) : projects.length === 0 ? (
            <div style={{ padding: '2rem', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textAlign: 'center' }}>
              <p style={{ color: '#666' }}>No projects found.</p>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>Click "Create Project" to add your first project.</p>
            </div>
          ) : (
            <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowY: 'scroll', overflowX: 'auto', height: '540px' }}>
              {projectFilter.getActiveFilters().length > 0 && (
                <div style={{ padding: '0.5rem 1rem', backgroundColor: '#d1fae5', borderBottom: '1px solid #c3e6cb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                  <span style={{ color: '#065f46' }}>
                    {projectFilter.getActiveFilters().length} active filter(s) — showing {filteredProjects.length} of {projects.length}
                  </span>
                  <button onClick={() => projectFilter.clearAllFilters()} style={{ padding: '2px 10px', fontSize: '0.75rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Clear All Filters</button>
                </div>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    {projectColumns.map(col => (
                      <th key={col.key} style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span onClick={() => projectSort.handleSort(col.key)} style={{ cursor: 'pointer', userSelect: 'none', flex: 1 }} title={`Sort by ${col.label}`}>
                            {col.label}
                            {projectSort.sortColumn === col.key && (<span style={{ fontSize: '0.7rem', marginLeft: '3px' }}>{projectSort.sortDirection === 'asc' ? '▲' : '▼'}</span>)}
                          </span>
                          <span onClick={(e) => projectFilter.openFilterAtPosition(e, col.key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <FilterIcon isActive={!!(projectFilter.columnFilters[col.key]?.length > 0 || projectFilter.filterText[col.key])} color="#666" />
                          </span>
                        </div>
                        {projectFilter.openFilterColumn === col.key && (
                          <FilterTemplateDropdown
                            columnKey={col.key}
                            columnLabel={col.label}
                            isDateColumn={col.type === 'date'}
                            columnFilters={projectFilter.columnFilters}
                            filterMode={projectFilter.filterMode}
                            filterText={projectFilter.filterText}
                            filterSearch={projectFilter.filterSearch}
                            filterButtonPositions={projectFilter.filterButtonPositions}
                            setOpenFilterColumn={projectFilter.setOpenFilterColumn}
                            setFilterSearch={projectFilter.setFilterSearch}
                            getUniqueValues={projectFilter.getUniqueValues}
                            handleFilterToggle={projectFilter.handleFilterToggle}
                            selectAllColumnFilter={projectFilter.selectAllColumnFilter}
                            clearColumnFilter={projectFilter.clearColumnFilter}
                            applyTextFilter={projectFilter.applyTextFilter}
                          />
                        )}
                      </th>
                    ))}
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map(project => (
                    <tr key={project.id}>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                        <strong>{project.job_number}</strong>
                      </td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                        {project.job_name}
                        {project.archived_at && (
                          <span style={{ marginLeft: '0.5rem', padding: '0.1rem 0.4rem', backgroundColor: '#e9ecef', color: '#6c757d', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600 }}>
                            Archived
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                        {project.client_name ? project.client_name : (
                          <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.85rem' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                        {project.division_display ? (
                          <span style={{ padding: '0.25rem 0.5rem', backgroundColor: '#6c757d', color: 'white', borderRadius: '4px', fontSize: '0.75rem' }}>
                            {project.division_display}
                          </span>
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.85rem' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                        {project.disciplines && project.disciplines.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                            {project.disciplines.map(disc => (
                              <span key={disc.id} style={{ padding: '0.125rem 0.375rem', backgroundColor: '#17a2b8', color: 'white', borderRadius: '4px', fontSize: '0.7rem' }}>
                                {disc.discipline_code}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.85rem' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                        {project.created_display}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {project.sharepoint_url && (
                            <a href={toSharePointHref(project.sharepoint_url)} target="_blank" rel="noopener noreferrer"
                              style={{ padding: '0.25rem 0.5rem', backgroundColor: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'none', whiteSpace: 'nowrap' }}
                              title="Open SharePoint site">
                              SharePoint
                            </a>
                          )}
                          <button onClick={() => handleManageProject(project)}
                            style={{ padding: '0.25rem 0.5rem', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                            Manage
                          </button>
                          {isSuperadmin && (project.archived_at ? (
                            <button onClick={() => handleRestoreProject(project)}
                              style={{ padding: '0.25rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                              Restore
                            </button>
                          ) : (
                            <button onClick={() => handleArchiveProject(project)}
                              style={{ padding: '0.25rem 0.5rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                              Archive
                            </button>
                          ))}
                          {isSuperadmin && (
                            <button onClick={() => handleDeleteProject(project)}
                              style={{ padding: '0.25rem 0.5rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredProjects.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No projects match your search or filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'estimates' && (
        <EstimateManagement isSuperadmin={isSuperadmin} />
      )}

      {activeTab === 'divisions' && isAdmin && (
        <DivisionManagement />
      )}

      {activeTab === 'permissions' && isAdmin && (
        <PermissionsTab permissions={permissions} />
      )}

      {activeTab === 'users' && isAdmin && (
        <UserProvisioningTab />
      )}

      {activeTab === 'employees' && isAdmin && (
        <div style={{ margin: '-2rem', marginTop: '-1rem' }}>
          <HiringLayoffDashboard embedded onBack={() => setActiveTab('users')} />
        </div>
      )}

      {activeTab === 'audit-log' && isAdmin && (
        <AuditLogTab />
      )}

      {activeTab === 'notifications' && isSuperadmin && (
        <div>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setNotifSubTab('recipients')}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                backgroundColor: notifSubTab === 'recipients' ? '#007bff' : '#f8f9fa',
                color: notifSubTab === 'recipients' ? 'white' : '#666',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: notifSubTab === 'recipients' ? '500' : '400'
              }}
            >
              Recipients
            </button>
            <button
              onClick={() => setNotifSubTab('email-log')}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                backgroundColor: notifSubTab === 'email-log' ? '#007bff' : '#f8f9fa',
                color: notifSubTab === 'email-log' ? 'white' : '#666',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: notifSubTab === 'email-log' ? '500' : '400'
              }}
            >
              Email Log
            </button>
            <button
              onClick={() => setNotifSubTab('friday-updates')}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                backgroundColor: notifSubTab === 'friday-updates' ? '#007bff' : '#f8f9fa',
                color: notifSubTab === 'friday-updates' ? 'white' : '#666',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: notifSubTab === 'friday-updates' ? '500' : '400'
              }}
            >
              Friday schedule updates
            </button>
          </div>
          {notifSubTab === 'recipients' && <NotificationRecipientsPage embedded />}
          {notifSubTab === 'email-log' && <EmailLogPage />}
          {notifSubTab === 'friday-updates' && <FridayUpdateRequestsPage />}
        </div>
      )}

      {activeTab === 'weekly-report' && isSuperadmin && (
        <WeeklyReportManager />
      )}

      {showMaestroImportModal && isAdmin && (
        <MaestroActualsImportModal onClose={() => setShowMaestroImportModal(false)} />
      )}

      {activeTab === 'settings' && isAdmin && (
        <div>
          <h3 style={{ marginBottom: '1rem' }}>System Settings</h3>
          <div style={{
            padding: '2rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              alignItems: 'stretch',
              gap: '1rem'
            }}>
              {/* Fabrication Locations Card */}
              <div style={SETTINGS_CARD}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>🏭 Fabrication Locations</h4>
                <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Manage custom fabrication locations that can be used in matrix data.
                </p>
                <button
                  onClick={() => setShowFabricationLocationsModal(true)}
                  style={SETTINGS_CARD_BUTTON}
                >
                  Edit Fabrication Locations
                </button>
              </div>

              {/* Priorities Card */}
              <div style={SETTINGS_CARD}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>🎯 Priority Labels</h4>
                <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Manage priority labels that can be assigned to matrix items.
                </p>
                <button
                  onClick={() => setShowPrioritiesModal(true)}
                  style={SETTINGS_CARD_BUTTON}
                >
                  Edit Priorities
                </button>
              </div>

              {/* Tags Card */}
              <div style={SETTINGS_CARD}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>🏷️ Tags</h4>
                <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Manage tag categories and tags used to label records across the portal (trackers, matrix lines, and more).
                </p>
                <button
                  onClick={() => setShowManageTagsModal(true)}
                  style={SETTINGS_CARD_BUTTON}
                >
                  Manage Tags
                </button>
              </div>

              {/* Disciplines Card */}
              <div style={SETTINGS_CARD}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>📋 Disciplines</h4>
                <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Manage discipline types that can be assigned to projects (e.g., Piping, Mechanical, Electrical).
                </p>
                <button
                  onClick={() => setShowDisciplinesModal(true)}
                  style={SETTINGS_CARD_BUTTON}
                >
                  Edit Disciplines
                </button>
              </div>

              {/* Sub-Departments Card */}
              <div style={SETTINGS_CARD}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>🏢 Departments & Sub-Departments</h4>
                <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Manage departments and their sub-departments (trades). Sub-departments control user module access.
                </p>
                <button
                  onClick={() => setShowSubDepartmentsModal(true)}
                  style={SETTINGS_CARD_BUTTON}
                >
                  Manage Sub-Departments
                </button>
              </div>

              {/* Maestro actuals (all projects) Card */}
              <div style={SETTINGS_CARD}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>📥 Maestro Actuals (all projects)</h4>
                <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Import one Maestro hours export for every job, review the reconciliation, and check the SharePoint drop folder.
                </p>
                <button
                  onClick={() => setShowMaestroImportModal(true)}
                  style={SETTINGS_CARD_BUTTON}
                >
                  Open Maestro Import
                </button>
              </div>

              {/* Chatbot Settings Card (Superadmin only) */}
              {user?.primary_role_id === 'superadmin' && (
                <div style={SETTINGS_CARD}>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>💬 Help Chatbot</h4>
                  <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                    Control the AI help chatbot visibility. Toggle globally, per role, or per user.
                  </p>
                  <button
                    onClick={() => setShowChatbotSettingsModal(true)}
                    style={SETTINGS_CARD_BUTTON}
                  >
                    Manage Chatbot
                  </button>
                </div>
              )}

              {/* Letterhead */}
              <div style={SETTINGS_CARD}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>📄 Letterhead</h4>
                <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Per-division legal-entity headers and the job-number prefix map used on every portal PDF.
                </p>
                <button
                  onClick={() => setShowLetterheadSettingsModal(true)}
                  style={SETTINGS_CARD_BUTTON}
                >
                  Manage Letterheads
                </button>
              </div>

              {/* Nomenclature */}
              <div style={SETTINGS_CARD}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>🔤 Nomenclature</h4>
                <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Standard filename, job-line, and SharePoint naming patterns. Project-type list for new jobs.
                </p>
                <button
                  onClick={() => setShowNomenclatureSettingsModal(true)}
                  style={SETTINGS_CARD_BUTTON}
                >
                  Manage Nomenclature
                </button>
              </div>

              {/* Accounting Card — superadmin only */}
              {permissions.isSuperadmin && (
                <div style={SETTINGS_CARD}>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>💰 Accounting</h4>
                  <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                    Manage accounting-related configuration such as currency exchange rates used in financial reports.
                  </p>
                  <button
                    onClick={() => setShowAccountingSettingsModal(true)}
                    style={SETTINGS_CARD_BUTTON}
                  >
                    Manage Accounting Settings
                  </button>
                </div>
              )}
            </div>
            <div style={{ marginTop: '2rem' }}>
              <DataTab />
            </div>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onProjectCreated={handleProjectCreated}
      />

      {/* Fabrication Locations Modal */}
      <FabricationLocationsModal
        isOpen={showFabricationLocationsModal}
        onClose={() => setShowFabricationLocationsModal(false)}
      />

      {/* Priorities Modal */}
      <PrioritiesModal
        isOpen={showPrioritiesModal}
        onClose={() => setShowPrioritiesModal(false)}
      />

      {/* Manage Tags Modal */}
      <ManageTagsModal
        isOpen={showManageTagsModal}
        onClose={() => setShowManageTagsModal(false)}
      />

      {/* Disciplines Modal */}
      <DisciplinesModal
        isOpen={showDisciplinesModal}
        onClose={() => setShowDisciplinesModal(false)}
      />

      {/* Sub-Departments Modal */}
      <SubDepartmentsModal
        isOpen={showSubDepartmentsModal}
        onClose={() => setShowSubDepartmentsModal(false)}
      />

      {/* Chatbot Settings Modal */}
      <ChatbotSettingsModal
        isOpen={showChatbotSettingsModal}
        onClose={() => setShowChatbotSettingsModal(false)}
      />

      {/* Accounting Settings Modal — superadmin only */}
      <AccountingSettingsModal
        isOpen={showAccountingSettingsModal}
        onClose={() => setShowAccountingSettingsModal(false)}
      />

      <LetterheadSettingsModal
        isOpen={showLetterheadSettingsModal}
        onClose={() => setShowLetterheadSettingsModal(false)}
      />

      <NomenclatureSettingsModal
        isOpen={showNomenclatureSettingsModal}
        onClose={() => setShowNomenclatureSettingsModal(false)}
      />

      {/* Delete Project Modal */}
      {selectedProject && showDeleteProjectModal && (
        <DeleteProjectModal
          project={selectedProject}
          onClose={() => {
            setShowDeleteProjectModal(false);
            setSelectedProject(null);
          }}
          onProjectDeleted={handleProjectDeleted}
        />
      )}

      {showProjectsHelp && (
        <ProjectManagementHelpModal onClose={() => setShowProjectsHelp(false)} />
      )}

      {/* Project Details Modal */}
      {selectedProject && showProjectDetailsModal && (
        <ProjectDetailsModal
          isOpen={showProjectDetailsModal}
          onClose={() => {
            setShowProjectDetailsModal(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
          onProjectUpdated={handleProjectUpdated}
          isSuperadmin={isSuperadmin}
        />
      )}
    </Box>
  );
};

export default AdminPanel;
